import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ESInput from "../../controls/ESInput";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import {
  getUserDepartmentById,
  insertUserDepartment,
  updateUserDepartment,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import {
  checkIfSuperAdmin,
  getCachedData,
  Option,
  queryClient
} from "../../utils/common";
import { showToast, ToastTypeCode } from "../../utils/Constant";
import Loader from "../loader/Loader";
import "./UserDepartmentList.scss";

interface UserDepartment {
  DepartmentId?: number;
  DepartmentName: string;
  IsActive: number;
  OrganizationId?: number;
  CreateUserId?: number;
  UpdateUserId?: number | null;
  CreateDate?: string;
  UpdateDate?: string | null;
  InternalKey?: string;
  ConcurrencyKey?: string;
}

interface AddUserDepartmentProps {
  departmentId?: number | null;
  onClose: () => void;
}

const AddUserDepartment: React.FC<AddUserDepartmentProps> = ({
  departmentId,
  onClose,
}) => {
  const methods = useForm<UserDepartment>({
    defaultValues: {
      DepartmentName: "",
      IsActive: 1,
    },
  });
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [organizationDropDownChange, setOrganizationDropDownChange] =
    useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<number>(0);
  const { appContextdata } = useContext<any>(UserContext);

  const fetchDepartmentDetailsById = async (departmentId: number) => {
    setLoading(true);
    try {
      const response: any = await axiosPost(getUserDepartmentById, {
        DepartmentId: departmentId,
      });

      if (response?.data?.Status === 200) {
        const data = response.data.ExtraData;
        const transformedData = {
          ...data,
          OrganizationId: organizations?.find(
            (org: Option) => org.value === data.OrganizationId
          )
            ? {
                label: organizations?.find(
                  (org: Option) => org.value === data.OrganizationId
                )?.label,
                value: data.OrganizationId,
              }
            : null,
        };
        methods.reset(transformedData);
        setSelectedOrganization(data.OrganizationId);
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error fetching department details", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        if (departmentId && organizations?.length >= 1) {
          fetchDepartmentDetailsById(departmentId);
        }
      } else {
        if (departmentId) {
          fetchDepartmentDetailsById(departmentId);
        }
      }
    }
  }, [appContextdata?.Role, departmentId, organizations, methods]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData('organizations')) as Option[];
        setOrganizations(options);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const onSubmit: SubmitHandler<UserDepartment> = async (data) => {
    const transformedData = {
      ...data,
      IsActive: data.IsActive ? 1 : 0,
      OrganizationId: selectedOrganization
        ? selectedOrganization
        : appContextdata?.Organization,
        CreateDate: new Date().toISOString()
    };
    setLoading(true);
    try {
      const response: any = departmentId
        ? await axiosPost(updateUserDepartment, transformedData)
        : await axiosPost(insertUserDepartment, transformedData);

      if (response?.data?.Status === 200) {
        queryClient.invalidateQueries("departments");
        const message = departmentId
          ? "Department updated successfully"
          : "Department created successfully";
        showToast(message, ToastTypeCode?.SUCCESS);
        onClose();
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error saving department", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = async (selectedOption: any) => {
    setOrganizationDropDownChange(true);
    const organizationId = selectedOption?.value;
    setSelectedOrganization(organizationId);
  };

  return (
    <div>
      <div className="sub-card">
        <div>
          {loading ? (
            <Loader />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Row>
                  {isSuperAdmin && (
                    <Form.Group as={Col} md="3">
                      <Form.Label>
                        Organization<span className="text-danger">*</span>
                      </Form.Label>
                      <ESSearchDropdown
                        name="OrganizationId"
                        placeholder="Organization"
                        options={organizations}
                        onChange={handleOrganizationChange}
                      />
                    </Form.Group>
                  )}
                  <Form.Group as={Col} md="3">
                    <Form.Label>
                      Department Name
                      <span className="text-danger">*</span>
                    </Form.Label>

                    <ESInput
                      className="form-control"
                      name="DepartmentName"
                      placeholder="Department name"
                      type="text"
                      regex={/^[A-Za-z\s]+$/}
                      required
                      minLength={2}
                      maxLength={200}
                    />
                  </Form.Group>
                  <Col md="2">
                    <div className="form-check">
                      <ESInput
                        name="IsActive"
                        type="checkbox"
                        placeholder={""}
                        className="form-check-input"
                      />
                      <label htmlFor="IsActive" className="form-check-label">Is Active</label>
                    </div>
                  </Col>
                  <Col md="3">
                    <button type="submit" className="save-btn">
                      {departmentId ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="back-btn"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </Col>
                </Row>
              </form>
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUserDepartment;
